import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { isPast } from 'date-fns';

// Routes that anyone can still access even when subscription is expired
const TRIAL_EXEMPT_PATHS = [
  '/subscription-billing',
  '/subscription-management',
  '/login',
  '/logout',
  '/paywall',
  '/reset-password',
  '/forgot-password',
  '/org-admin-first-login-checklist',
];

// Routes that Org Admin/Owner can access when subscription is expired
// (in addition to TRIAL_EXEMPT_PATHS)
export const EXPIRED_ADMIN_ALLOWED_PATHS = [
  '/dashboard',
  '/organization-settings',
  '/subscription-billing',
  '/subscription-management',
  '/login',
  '/logout',
  '/paywall',
  '/reset-password',
  '/forgot-password',
  '/org-admin-first-login-checklist',
];

// Maximum time to wait for subscription check before unblocking
const TRIAL_CHECK_TIMEOUT_MS = 6000;

/**
 * useTrialGuard
 *
 * Returns { blocked, loading, onActivated } for the current authenticated user.
 * - blocked: true when the org's subscription has expired/suspended/past_due/cancelled
 *            AND the current route is not in TRIAL_EXEMPT_PATHS.
 * - blockedAsNonAdmin: true when the user is NOT an org_admin and is blocked
 *                      (non-admins are redirected to /subscription-billing instead of seeing the modal)
 * - blockedAsAdmin: true when the user IS an org_admin/owner and is blocked but on a
 *                   route not in EXPIRED_ADMIN_ALLOWED_PATHS
 * - loading: true while the subscription status is being fetched.
 * - onActivated: callback to call after the user successfully upgrades so the block is lifted.
 *
 * Superadmins are never blocked.
 *
 * When a subscription is found to be expired/suspended, all non-admin org_members
 * are automatically set to is_active = false in the org_members table.
 */
const useTrialGuard = () => {
  const { userProfile, isSuperAdmin } = useAuth();
  const location = useLocation();
  const [blocked, setBlocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const safetyTimerRef = useRef(null);
  // Track whether we've already deactivated members for this org to avoid repeated DB writes
  const deactivatedRef = useRef(false);

  const isOrgAdmin = userProfile?.system_role === 'org_admin';
  const isExemptPath = TRIAL_EXEMPT_PATHS?.some(
    (p) => location?.pathname === p || location?.pathname?.startsWith(p + '/')
  );

  const checkSubscription = useCallback(async () => {
    // Superadmins are never blocked
    if (isSuperAdmin || isExemptPath) {
      setBlocked(false);
      setLoading(false);
      return;
    }

    // Only check if user has a profile with an org
    if (!userProfile?.organization_id) {
      setBlocked(false);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Safety valve: never block the app for more than TRIAL_CHECK_TIMEOUT_MS
    if (safetyTimerRef?.current) clearTimeout(safetyTimerRef?.current);
    safetyTimerRef.current = setTimeout(() => {
      setLoading(false);
    }, TRIAL_CHECK_TIMEOUT_MS);

    try {
      const orgId = userProfile?.organization_id;
      if (!orgId) {
        setBlocked(false);
        return;
      }

      const { data: sub } = await supabase
        ?.from('org_subscriptions')
        ?.select('status, trial_ends_at, account_suspended_at, payment_method_added')
        ?.eq('organization_id', orgId)
        ?.maybeSingle();

      if (!sub) {
        setBlocked(false);
        return;
      }

      const suspended = !!sub?.account_suspended_at;
      const status = sub?.status;
      const trialEnd = sub?.trial_ends_at ? new Date(sub.trial_ends_at) : null;

      const trialExpired =
        status === 'trialing' && trialEnd && isPast(trialEnd);

      const pastDueOrCancelled =
        status === 'past_due' || status === 'cancelled';

      const isExpired = trialExpired || suspended || pastDueOrCancelled;

      setBlocked(isExpired);

      // When subscription is expired, deactivate all non-admin org_members
      // Only do this once per session to avoid repeated DB writes
      if (isExpired && !deactivatedRef?.current) {
        deactivatedRef.current = true;
        try {
          // Deactivate all org_members who are NOT owner or admin role
          await supabase
            ?.from('org_members')
            ?.update({ is_active: false })
            ?.eq('organization_id', orgId)
            ?.not('role', 'in', '("owner","admin")');
        } catch (deactivateErr) {
          console.warn('[TrialGuard] Failed to deactivate member users:', deactivateErr?.message);
        }
      }

      // When subscription becomes active again (payment method added), reactivate members
      if (!isExpired && sub?.payment_method_added) {
        deactivatedRef.current = false;
      }
    } catch (err) {
      console.error('[TrialGuard] Subscription check error:', err?.message);
      setBlocked(false);
    } finally {
      if (safetyTimerRef?.current) {
        clearTimeout(safetyTimerRef?.current);
        safetyTimerRef.current = null;
      }
      setLoading(false);
    }
  }, [isSuperAdmin, isExemptPath, userProfile?.organization_id]);

  useEffect(() => {
    if (userProfile) {
      checkSubscription();
    } else {
      // No profile yet — don't block
      setLoading(false);
      setBlocked(false);
    }

    return () => {
      if (safetyTimerRef?.current) clearTimeout(safetyTimerRef?.current);
    };
  }, [userProfile, checkSubscription]);

  const onActivated = useCallback(async () => {
    setBlocked(false);
    // Reactivate all org_members when subscription is restored
    if (userProfile?.organization_id) {
      deactivatedRef.current = false;
      try {
        await supabase
          ?.from('org_members')
          ?.update({ is_active: true })
          ?.eq('organization_id', userProfile?.organization_id)
          ?.not('role', 'in', '("owner","admin")');
      } catch (reactivateErr) {
        console.warn('[TrialGuard] Failed to reactivate member users:', reactivateErr?.message);
      }
    }
  }, [userProfile?.organization_id]);

  // blockedAsNonAdmin: non-admin users who are blocked should be redirected, not shown the modal
  const blockedAsNonAdmin = blocked && !isOrgAdmin;

  // blockedAsAdmin: org_admin/owner users who are blocked AND on a route not in the allowed list
  const isOnAllowedAdminPath = EXPIRED_ADMIN_ALLOWED_PATHS?.some(
    (p) => location?.pathname === p || location?.pathname?.startsWith(p + '/')
  );
  const blockedAsAdmin = blocked && isOrgAdmin && !isOnAllowedAdminPath;

  return { blocked, blockedAsNonAdmin, blockedAsAdmin, loading, onActivated, refresh: checkSubscription };
};

export default useTrialGuard;
